import _ from 'underscore';
import { Injectable } from '@angular/core';

import { ClientResource } from '../resources/client-resource';

const DEDUP_WINDOW_MS = 60_000;
const MAX_CACHE_SIZE = 500;

// According to https://angular.io/guide/singleton-services, providedIn root
// tells angular to initialize a singleton service.  In such way, no need to
// register it under module providers.
@Injectable({
  providedIn: 'root',
})
export class Logger {
  private recentLogs = new Map<string, number>();

  constructor(private clientResource: ClientResource) {}

  async log(
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    payload?: any,
  ) {
    const fingerprint = buildFingerprint(level, message, payload);
    const now = Date.now();
    const lastSent = this.recentLogs.get(fingerprint);

    if (lastSent !== undefined && now - lastSent < DEDUP_WINDOW_MS) {
      console.debug(
        `[Logger] Suppressed duplicate log "${level}: ${message}" (last sent ${now - lastSent}ms ago)`,
      );
      return;
    }

    this.recentLogs.set(fingerprint, now);

    // Prune old entries and cap cache size
    if (this.recentLogs.size > MAX_CACHE_SIZE) {
      const cutoff = now - DEDUP_WINDOW_MS;
      for (const [key, timestamp] of this.recentLogs) {
        if (timestamp < cutoff) {
          this.recentLogs.delete(key);
        }
      }
      // If still too large after pruning old entries, clear oldest half
      if (this.recentLogs.size > MAX_CACHE_SIZE) {
        const entries = Array.from(this.recentLogs.entries());
        entries.sort((a, b) => a[1] - b[1]);
        const toDelete = entries.slice(0, entries.length - MAX_CACHE_SIZE / 2);
        for (const [key] of toDelete) {
          this.recentLogs.delete(key);
        }
      }
    }

    const payloadStr = JSON.stringify(standardErrorConverter(payload));
    return await this.clientResource.emitClientLog({
      level,
      message,
      payload: payloadStr,
    });
  }

  async debug(message: string, payload?: any) {
    return this.log('debug', message, payload);
  }

  async info(message: string, payload?: any) {
    return this.log('info', message, payload);
  }

  async warn(message: string, payload?: any) {
    return this.log('warn', message, payload);
  }

  async error(message: string, payload?: any) {
    return this.log('error', message, payload);
  }
}

function buildFingerprint(
  level: string,
  message: string,
  payload?: any,
): string {
  const errorObj = payload instanceof Error
    ? { name: payload.name, message: payload.message }
    : null;
  return `${level}::${message}::${errorObj?.name ?? ''}::${errorObj?.message ?? ''}`;
}

export function standardErrorConverter(err: any): ErrorObject {
  if (err instanceof Error) {
    return {
      type: 'Error',
      name: err.constructor.name,
      message: err.message,
      stack: err.stack,
    };
  }

  return {
    type: 'unknown',
    raw: JSON.stringify(err),
  };
}

export interface ErrorObject {
  type: string;
  name?: string;
  message?: string;
  stack?: string;
  raw?: string;
}
